import axios from 'axios';

const baseURL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

// Attach session ID to all requests
const sessionId = crypto.randomUUID();
api.interceptors.request.use(config => {
  config.headers['X-Session-ID'] = sessionId;
  return config;
});

export interface SAIFileResponse {
  id: number;
  filename: string;
  file_size: number;
  datapoint_count: number;
  model_count: number;
  image?: string;
  parameters?: any[];
  trained_models?: any[];
  uploaded_at: string;
}

export interface VisualizeResponse {
  axes: Record<string, number[]>;
  output: {
    name: string;
    unit: string;
    values: number[];
  };
  resolution: number;
}

export const saiFilesApi = {
  list: (): Promise<SAIFileResponse[]> =>
    api.get('/files/').then(res => res.data),

  create: (file: File): Promise<SAIFileResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/files/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(res => res.data);
  },

  getDetail: (id: number): Promise<SAIFileResponse> =>
    api.get(`/files/${id}/`).then(res => res.data),

  delete: (id: number): Promise<void> =>
    api.delete(`/files/${id}/`).then(() => undefined),

  train: (id: number, params: any): Promise<any> =>
    api.post(`/files/${id}/train`, params).then(res => res.data),

  saveModel: (id: number, modelData: any): Promise<SAIFileResponse> =>
    api.post(`/files/${id}/save-model`, modelData).then(res => res.data),

  update: (id: number, data: any, isFormData: boolean = false): Promise<SAIFileResponse> => {
    if (isFormData) {
      return api.patch(`/files/${id}/`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      }).then(res => res.data);
    }
    return api.patch(`/files/${id}/`, data).then(res => res.data);
  },

  addData: (id: number, data: any): Promise<SAIFileResponse> =>
    api.post(`/files/${id}/add-data`, data).then(res => res.data),
};

export const trainedModelsApi = {
  predict: (id: number, input: number[]): Promise<{ prediction: number }> =>
    api.post(`/models/${id}/predict`, { input }).then(res => res.data),

  design: (id: number, conditions: Record<string, string>, fixedValues: Record<string, number>): Promise<any> =>
    api.post(`/models/${id}/design`, { conditions, fixed_values: fixedValues }).then(res => res.data),

  savePrediction: (id: number, input: number[], output: number[]): Promise<{ message: string; model_name: string }> =>
    api.post(`/models/${id}/save-prediction`, { input, output }).then(res => res.data),

  saveDesign: (id: number, conditions: Record<string, string>, fixedValues: Record<string, number>, designOutput: Record<string, number[]>): Promise<{ message: string; model_name: string }> =>
    api.post(`/models/${id}/save-design`, { conditions, fixed_values: fixedValues, design_output: designOutput }).then(res => res.data),

  visualize: (
    id: number,
    selectedParams: string[],
    fixedValues: Record<string, number>,
    resolution: number
  ): Promise<VisualizeResponse> =>
    api.post(`/models/${id}/visualize`, {
      selected_params: selectedParams,
      fixed_values: fixedValues,
      resolution,
    }).then(res => res.data),
};

export interface ChatSessionState {
  sai_file_id: number | null;
  sai_file_name: string | null;
  model_index: number | null;
  model_name: string | null;
  current_page: string | null;
  approach: 'existing' | 'custom' | null;
  selected_algorithm: string | null;
  selected_model_mode: 'prediction' | 'design' | null;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatResponse {
  reply: string;
  session_state: ChatSessionState;
  action?: string;
  action_data?: Record<string, any>;
  prediction_result?: any;
  design_result?: any;
  training_result?: any;
  visualization?: any;
}

export const chatApi = {
  sendMessage: (
    message: string,
    history: ChatMessage[],
    sessionState: ChatSessionState
  ): Promise<ChatResponse> =>
    api.post('/chat/message/', {
      message,
      history,
      session_state: sessionState,
    }).then(res => res.data),

  sync: (data: {
    wizard_context: Record<string, any>;
    recent_actions: Array<{ action: string; detail: any; timestamp: string }>;
    sai_file_id?: number;
    model_index?: number;
  }): Promise<void> =>
    api.post('/chat/sync/', data).then(() => undefined),
};
